// StatTile — a headline number with context.
//
// A number alone ("QAR 4,120") is nearly useless to an operator: they need to
// know whether it is up or down on the comparable period. Every tile therefore
// takes an optional `delta`, and renders the comparison explicitly rather than
// leaving the reader to guess the baseline.
//
// 🔴 Colour is NOT hardcoded to "up = green". For churn, refunds and failed
// payments an increase is bad. `deltaGood` says which direction is good, and
// it has no default — the caller must decide, because getting it wrong turns a
// dashboard into misinformation.

import { cn } from '@fajrarabi/ui';
import type { ReactNode } from 'react';

import { Icon, type IconName } from './Icon';

export interface StatTileProps {
  label: string;
  /** Pre-formatted. Money must come through <Money>, never raw division. */
  value: ReactNode;
  /** Fractional change vs the comparison period (0.12 = +12%). */
  delta?: number | null;
  /** Which direction is GOOD. Required whenever `delta` is set. */
  deltaGood?: 'up' | 'down';
  /** What the delta is measured against, e.g. "vs previous 30 days". */
  deltaLabel?: string;
  icon?: IconName;
  /** Secondary line under the value. */
  hint?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function StatTile({
  label,
  value,
  delta,
  deltaGood,
  deltaLabel,
  icon,
  hint,
  loading = false,
  className,
}: StatTileProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-admin-200 bg-white p-4 transition-shadow hover:shadow-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-admin-500">{label}</p>
        {icon && (
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-admin-50 text-admin-400">
            <Icon name={icon} className="h-4 w-4" />
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-2.5 h-7 w-24 animate-pulse rounded bg-admin-100" />
      ) : (
        <p className="mt-2 text-2xl font-bold tracking-tight text-admin-900">{value}</p>
      )}

      {(delta != null || hint) && !loading && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          {delta != null && deltaGood && <DeltaChip delta={delta} good={deltaGood} />}
          {deltaLabel && delta != null && <span className="text-admin-400">{deltaLabel}</span>}
          {hint && <span className="text-admin-500">{hint}</span>}
        </div>
      )}
    </div>
  );
}

function DeltaChip({ delta, good }: { delta: number; good: 'up' | 'down' }) {
  // A flat period is neither good nor bad; colouring it would imply a trend
  // that is not there.
  const flat = Math.abs(delta) < 0.0005;
  const up = delta > 0;
  const isGood = flat ? null : (up && good === 'up') || (!up && good === 'down');

  const pct = `${up && !flat ? '+' : ''}${(delta * 100).toFixed(1)}%`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-medium tabular-nums',
        flat && 'bg-admin-100 text-admin-600',
        isGood === true && 'bg-success-50 text-success-700',
        isGood === false && 'bg-error-50 text-error-700',
      )}
    >
      {!flat && <span aria-hidden>{up ? '↑' : '↓'}</span>}
      {flat ? 'No change' : pct}
    </span>
  );
}

/** Responsive grid for a row of tiles. */
export function StatGrid({
  children,
  className,
  columns = 4,
}: {
  children: ReactNode;
  className?: string;
  columns?: 2 | 3 | 4;
}) {
  return (
    <div
      className={cn(
        'grid gap-3',
        // Complete class names — Tailwind cannot see an interpolated
        // `grid-cols-${n}` (documented trap).
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        columns === 4 && 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
        className,
      )}
    >
      {children}
    </div>
  );
}
